import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableHead,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { InvoiceTitleRow } from "./InvoiceTitleRow";
import { InvoiceContentRow } from "./InvoiceContentRow";
import {
  EezyCostCenter,
  InvoiceEdit,
  InvoiceRow,
  TYPE_EXTERNAL,
} from "../invoiceAPI";
import React, { useCallback, useEffect, useState } from "react";
import { CustomerCostCenter } from "../../customer/customerAPI";
import { fetchSalesAccountConfigForDate } from "../../settings/settingsActions";
import { handleSalesAccountsData } from "../../settings/helpers/utils";
import { SalesAccount } from "../../settings/settingsApi";
import { useAppDispatch } from "../../../app/hooks";
import {
  fetchEezyCompanies,
  fetchCustomerOrgById,
} from "../invoiceDetailedSlice";
import formatNumber from "../../../common/helpers/formatNumber";
import { isVatRatePercentZero, calculateInvoiceTotals } from "../utils";
import InvoiceVatLabelString from "../common/InvoiceVatLabelString";
import InvoiceVatValueString from "../common/InvoiceVatValueString";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";

interface Props {
  invoice: InvoiceEdit;
  updateInvoiceRows: (rows: InvoiceRow[], areValid: boolean) => void;
}

export const InvoiceContent = ({ invoice, updateInvoiceRows }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("invoices");

  const [isSalesAccountsLoading, setIsSalesAccountsLoading] =
    useState<boolean>(false);
  const [isCustomerCCsLoading, setIsCustomerCCsLoading] =
    useState<boolean>(false);
  const [isEezyCCsLoading, setIsEezyCCsLoading] = useState<boolean>(false);

  // After credit invoice is created, the new invoice may not have invoiceRows -> use empty array as fallback
  const [rows, setRows] = useState<InvoiceRow[]>(invoice.invoiceRows || []);
  const [salesAccountOptions, setSalesAccountOptions] = useState<
    SalesAccount[]
  >([]);
  const [customerCCs, setCustomerCCs] = useState<CustomerCostCenter[]>([]);
  const [eezyBuyerCCs, setEezyBuyerCCs] = useState<EezyCostCenter[]>([]);
  const [sellerCostCenters, setSellerCostCenters] = useState<EezyCostCenter[]>(
    [],
  );

  const { vatExcludedSum, vatSums, totalSum } = calculateInvoiceTotals(rows);

  const columns = [
    {
      invoiceFieldName: "articleName",
      translationKey: "articleName",
    },
    {
      invoiceFieldName: "articleCategory",
      translationKey: "articleCategory",
    },
    {
      invoiceFieldName: "eezyCostCenter",
      translationKey: "sellerCostCenter",
    },
    {
      invoiceFieldName: "salesAccountCode",
      translationKey: "salesAccount",
    },
    {
      invoiceFieldName: "quantityAmount",
      translationKey: "delivered",
    },
    {
      invoiceFieldName: "quantityUnit",
      translationKey: "unit",
    },
    {
      invoiceFieldName: "unitPriceAmount",
      translationKey: "unitPrice",
    },
    {
      invoiceFieldName:
        invoice.invoiceType === TYPE_EXTERNAL
          ? "customerCostCenter"
          : "eezyBuyerCostCenter",
      translationKey: "buyerCostCenter",
    },
    {
      invoiceFieldName: "discountPercent",
      translationKey: "discountPercent",
    },
    {
      invoiceFieldName: "vatRatePercent",
      translationKey: "vatPercent",
    },
    {
      invoiceFieldName: "vatCode",
      translationKey: "vatCode",
    },
    {
      invoiceFieldName: "vatExcludedAmount",
      translationKey: "taxFreeEuro",
    },
    {
      invoiceFieldName: "vatAmount",
      translationKey: "vatAmountEuro",
    },
    {
      invoiceFieldName: "amount",
      translationKey: "sumEuro",
    },
  ];

  const emptyInvoiceRow: InvoiceRow = {
    id: nanoid(),
    eezyCostCenterId: "",
    salesAccountCode: "",
    quantityAmount: "0",
    quantityUnit: "",
    unitPriceAmount: "0",
    customerOrganizationId: invoice.customerOrganizationId
      ? invoice.customerOrganizationId
      : undefined,
    customerCostCenterId: "",
    vatRatePercent: "0.00",
    vatCode: "",
    vatExcludedAmount: "",
    vatAmount: "",
    amount: "",
    articleName: "",
    articleCategory: "",
    freeText: "",
    editable: true,
  };

  const addRow = () => {
    setRows([...rows, { ...emptyInvoiceRow, id: nanoid() }]);
  };

  // https://react.dev/reference/react/useCallback#updating-state-from-a-memoized-callback
  const updateRow = useCallback((newRow: InvoiceRow) => {
    setRows((rows) =>
      rows?.map((row) => (row.id === newRow.id ? newRow : row)),
    );
  }, []);

  const copyRow = (id: string) => {
    const row = rows?.filter((row) => row.id === id)[0];
    const newRow = { ...row, id: nanoid(), editable: true };
    setRows([...rows, newRow]);
  };

  const deleteRow = (id: string) => {
    const newRows = rows?.filter((row) => row.id !== id);
    setRows(newRows);
  };

  // Validate rows when they change and update invoice rows
  useEffect(() => {
    let rowContentsValid = true;
    rows?.forEach((row) => {
      if (
        !row.eezyCostCenterId ||
        (invoice.invoiceType === TYPE_EXTERNAL
          ? !row.customerCostCenterId || !row.customerOrganizationId
          : !row.eezyBuyerCostCenterId) ||
        !row.salesAccountCode ||
        !row.quantityAmount ||
        !row.quantityUnit ||
        !row.unitPriceAmount ||
        !row.vatRatePercent ||
        !row.vatCode ||
        !row.vatExcludedAmount ||
        !row.vatAmount ||
        !row.amount ||
        (row.freeText && row.freeText.length > 512)
      ) {
        rowContentsValid = false;
        return;
      }
    });
    updateInvoiceRows(rows, rowContentsValid);
  }, [rows, invoice.invoiceType, updateInvoiceRows]);

  useEffect(() => {
    // Fetch seller and buyer cost centers
    setIsEezyCCsLoading(true);
    dispatch(fetchEezyCompanies({})).then((action) => {
      if (isFulfilled(action)) {
        const seller = action.payload.find(
          (eezyCompany) => eezyCompany.eezyCompanyId === invoice.eezyCompanyId,
        );
        if (!!seller) {
          setSellerCostCenters(seller.costCenters);
        }
        if (!!invoice.eezyBuyerCompanyId) {
          const eezyBuyer = action.payload.find(
            (eezyCompany) =>
              eezyCompany.eezyCompanyId === invoice.eezyBuyerCompanyId,
          );
          setEezyBuyerCCs(eezyBuyer?.costCenters || []);
        }
      }
      setIsEezyCCsLoading(false);
    });

    if (!!invoice.customerOrganizationId) {
      setIsCustomerCCsLoading(true);
      dispatch(
        fetchCustomerOrgById({ id: invoice.customerOrganizationId }),
      ).then((action) => {
        if (isFulfilled(action)) {
          setCustomerCCs(action.payload.costCenters || []);
        }
        setIsCustomerCCsLoading(false);
      });
    }

    // Fetch sales accounts
    const currentDate = format(new Date(), isoFormat);
    setIsSalesAccountsLoading(true);
    dispatch(fetchSalesAccountConfigForDate({ validFrom: currentDate })).then(
      (action) => {
        if (isFulfilled(action)) {
          const accounts = handleSalesAccountsData(
            action.payload.accounts,
          ).filter((account) =>
            invoice.invoiceType === TYPE_EXTERNAL
              ? account.canUseInExternalSales
              : account.canUseInGroupInternalSales,
          );
          setSalesAccountOptions(accounts);
          setIsSalesAccountsLoading(false);
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Typography
        variant="header"
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {t("invoiceContentAndSums")}
      </Typography>
      <Box sx={{ marginTop: "15px" }}>
        <Box>
          {isSalesAccountsLoading ||
          isEezyCCsLoading ||
          isCustomerCCsLoading ? (
            <LoadingSpinner />
          ) : (
            <Table>
              <TableHead>
                <InvoiceTitleRow columns={columns} />
              </TableHead>
              <TableBody>
                {rows?.map((row) => (
                  <InvoiceContentRow
                    key={row.id}
                    row={row}
                    customerOrgId={invoice.customerOrganizationId}
                    sellerCCs={sellerCostCenters}
                    onDelete={deleteRow}
                    onUpdate={updateRow}
                    onCopy={copyRow}
                    invoiceType={invoice.invoiceType}
                    customerCCs={customerCCs}
                    eezyBuyerCCs={eezyBuyerCCs}
                    salesAccounts={salesAccountOptions}
                    actionsMenuDisabled={false}
                  />
                ))}
              </TableBody>
            </Table>
          )}
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            sx={{ borderRadius: "18px", marginTop: "15px" }}
            onClick={addRow}
          >
            {t("add", { ns: "common" })}
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <Grid item container xs={12}>
            <Grid item xs={9}>
              <Typography fontWeight={700} align="right">
                {t("vatExcludedAmount")}
                {t("colonSeparator")}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography fontWeight={700} align="left" marginLeft="4rem">
                {formatNumber(vatExcludedSum.toString())} {t("euroUnit")}
              </Typography>
            </Grid>
            {vatSums.map((vatSum) => (
              <React.Fragment
                key={`${vatSum.vatRatePercent}_${vatSum.vatCode}`}
              >
                <Grid item xs={9}>
                  <Typography fontWeight={700} align="right">
                    <InvoiceVatLabelString vatSum={vatSum} />
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography fontWeight={700} align="left" marginLeft="4rem">
                    <InvoiceVatValueString vatSum={vatSum} />
                  </Typography>
                </Grid>
                {isVatRatePercentZero(vatSum.vatRatePercent) && (
                  <Grid item xs={9}>
                    <Typography align="right">{vatSum.vatFreeText}</Typography>
                  </Grid>
                )}
              </React.Fragment>
            ))}
            <Grid item xs={9}>
              <Typography fontWeight={700} align="right">
                {t("invoiceSum")}
                {t("colonSeparator")}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography fontWeight={700} align="left" marginLeft="4rem">
                {formatNumber(totalSum.toString())} {t("euroUnit")}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
